import { apiUrl } from "../components/ApiManager/AxiosHeaders";
import { fetchCommissionInvoice } from "../components/reports/commissions/commissionsApi";
import { mapCommissionInvoiceToWizardRecord } from "../components/reports/commissions/commissionInvoiceRecordMapper";
import { isFinanceInvoiceApiSuccess } from "./invoiceCreateHelpers";
import {
  buildClientDisplayProfile,
  mergeInvoiceClientProfiles,
  normalizeClientRecordSource,
} from "./applicationCostHelpers";
import {
  buildInvoiceXeroPreviewDraftFromRecord,
  normalizeFinanceInvoiceRecord,
} from "./invoiceEditHelpers";
import {
  mapRowToInvoiceForm,
  normalizeInvoiceSettingRecord,
} from "./invoiceSettingHelpers";
import { buildInvoiceEmailHtml } from "./invoiceEmailHtmlHelpers";
import { mapWithConcurrency } from "./mapWithConcurrency";

async function resolveInvoiceSettingForPreview() {
  const settingResponse = await apiUrl.get("/getinvoiceSetting");
  return (
    normalizeInvoiceSettingRecord(settingResponse) ||
    (settingResponse?.status
      ? mapRowToInvoiceForm(settingResponse?.data?.data || {})
      : null)
  );
}

async function loadCommissionInvoicePreviewDrafts(invoiceIds = []) {
  const ids = (invoiceIds || []).map(String).filter(Boolean);
  if (!ids.length) return [];

  const invoiceSetting = await resolveInvoiceSettingForPreview();
  const prepared = await mapWithConcurrency(ids, async (invoiceId) => {
    try {
      const full = await fetchCommissionInvoice(invoiceId);
      const invoice = mapCommissionInvoiceToWizardRecord(full);
      const draft = buildInvoiceXeroPreviewDraftFromRecord(invoice, {
        invoiceSetting,
      });
      if (!draft) return null;

      return {
        invoice_id: invoice?.id || invoiceId,
        invoice_number: draft.invoiceNumber || invoice?.invoice_number || "",
        client_id: invoice?.client_id || invoice?.clientId || null,
        draft,
        html: buildInvoiceEmailHtml(draft),
      };
    } catch (error) {
      console.error(
        `Failed to load commission invoice preview for ${invoiceId}`,
        error,
      );
      return null;
    }
  });

  return prepared.filter(Boolean);
}

export function isFinanceInvoiceEmailApiSuccess(response) {
  return Boolean(
    response?.data?.success ||
      response?.data?.status ||
      response?.status === 200 ||
      response?.status === 201,
  );
}

export function getFinanceInvoiceEmailApiMessage(
  response,
  fallback = "Unable to send invoice email.",
) {
  return (
    response?.data?.message ||
    response?.data?.error ||
    response?.data?.msg ||
    fallback
  );
}

export async function resolveInvoicePreviewClientProfile({
  clientProfile = null,
  clientId = null,
} = {}) {
  if (clientProfile?.fullName || clientProfile?.email || clientProfile?.phone) {
    return clientProfile;
  }

  const resolvedClientId = String(clientId || clientProfile?.id || "").trim();
  if (!resolvedClientId) return clientProfile;

  try {
    const clientResponse = await apiUrl.get(`viewClientDetails/${resolvedClientId}`);
    const clientData = clientResponse?.data?.data || null;
    const { record, addressList } = normalizeClientRecordSource(clientData);
    return buildClientDisplayProfile(record, addressList);
  } catch (error) {
    console.error("Failed to load client profile for invoice preview", error);
    return clientProfile;
  }
}

export async function loadInvoicePreviewDrafts(
  invoiceIds = [],
  { clientProfile = null, clientId = null, isCommission = false } = {},
) {
  const ids = (invoiceIds || []).map(String).filter(Boolean);
  if (!ids.length) return [];

  if (isCommission) {
    return loadCommissionInvoicePreviewDrafts(ids);
  }

  const settingPromise = apiUrl.get("/getinvoiceSetting");
  const discountRulesPromise = apiUrl.get("getAllDiscountRules");
  const invoicesPromise = mapWithConcurrency(ids, (invoiceId) =>
    apiUrl.get(`clientFinanceInvoices/${invoiceId}`),
  );
  const clientProfilePromise = resolveInvoicePreviewClientProfile({
    clientProfile,
    clientId,
  });

  const [
    settingResponse,
    discountRulesResponse,
    resolvedClientProfile,
    invoiceResponses,
  ] = await Promise.all([
    settingPromise,
    discountRulesPromise,
    clientProfilePromise,
    invoicesPromise,
  ]);

  const discountFeeRules = Array.isArray(discountRulesResponse?.data?.data)
    ? discountRulesResponse.data.data
    : Array.isArray(discountRulesResponse?.data)
      ? discountRulesResponse.data
      : [];

  const invoiceSetting =
    normalizeInvoiceSettingRecord(settingResponse) ||
    (settingResponse?.status
      ? mapRowToInvoiceForm(settingResponse?.data?.data || {})
      : null);

  const preparedDrafts = invoiceResponses
    .map((response, index) => {
      if (!isFinanceInvoiceApiSuccess(response)) return null;
      const invoice = normalizeFinanceInvoiceRecord(response);
      const draft = buildInvoiceXeroPreviewDraftFromRecord(invoice, {
        invoiceSetting,
        discountFeeRules,
      });
      if (!draft) return null;

      return {
        invoice_id: invoice?.id || ids[index],
        invoice_number: draft.invoiceNumber || "",
        client_id:
          invoice?.client_id ||
          invoice?.clientId ||
          invoice?.client_snapshot?.id ||
          invoice?.client_snapshot?.client_id ||
          null,
        draft,
      };
    })
    .filter(Boolean);

  const clientIds = [
    ...new Set(
      preparedDrafts
        .map((item) => String(item?.client_id || "").trim())
        .filter(Boolean),
    ),
  ];

  let profileByClientId = {};

  if (resolvedClientProfile && String(clientId || resolvedClientProfile?.id || "").trim()) {
    const key = String(clientId || resolvedClientProfile?.id || "").trim();
    profileByClientId = { [key]: resolvedClientProfile };
  } else if (clientIds.length) {
    const entries = await mapWithConcurrency(clientIds, async (id) => [
      id,
      await resolveInvoicePreviewClientProfile({ clientId: id }),
    ]);
    profileByClientId = Object.fromEntries(entries);
  }

  return preparedDrafts.map((item) => {
    const mergedDraft = { ...item.draft };
    const clientKey = String(item.client_id || "").trim();
    const liveProfile = profileByClientId[clientKey] || null;

    if (liveProfile) {
      mergedDraft.clientProfile = mergeInvoiceClientProfiles(
        mergedDraft.clientProfile,
        liveProfile,
      );
    }

    return {
      invoice_id: item.invoice_id,
      invoice_number: item.invoice_number,
      draft: mergedDraft,
      html: buildInvoiceEmailHtml(mergedDraft),
    };
  });
}

export function buildSendInvoicePayload({
  clientId,
  clientName,
  clientEmail,
  invoices = [],
}) {
  return {
    client_id: clientId,
    name: clientName || "",
    email: clientEmail || "",
    invoices: (invoices || []).map((item) => ({
      invoice_id: item.invoice_id,
      invoice_number: item.invoice_number || "",
      html: item.html || "",
    })),
  };
}
